interface FeatureFlagConfig {
  percentage: number; // 0-100 rollout percentage
  enabledUsers?: string[]; // Specific user IDs
  enabledEstablishments?: string[]; // Specific establishment IDs
  startDate?: string; // ISO date when to start rollout
  endDate?: string; // ISO date when to complete rollout
}

interface FeatureFlagOverride {
  [key: string]: boolean | FeatureFlagConfig;
}

interface FeatureFlagMetrics {
  apiCalls: number;
  mockCalls: number;
  errors: number;
  avgResponseTime: number;
  lastError?: string;
  lastErrorTimestamp?: number;
}

const isTestEnv =
  typeof process !== 'undefined' &&
  (process.env['NODE_ENV'] === 'test' || process.env['VITEST'] !== undefined);

class FeatureFlagManager {
  private static instance: FeatureFlagManager;
  private overrides: FeatureFlagOverride = {};
  private metrics: Record<string, FeatureFlagMetrics> = {};
  private userId?: string;
  private establishmentId?: string;

  private constructor() {
    this.loadFromLocalStorage();
    this.initializeMetrics();
  }

  static getInstance(): FeatureFlagManager {
    if (!FeatureFlagManager.instance) {
      FeatureFlagManager.instance = new FeatureFlagManager();
    }
    return FeatureFlagManager.instance;
  }

  setUserContext(userId: string, establishmentId: string) {
    this.userId = userId;
    this.establishmentId = establishmentId;
  }

  isFeatureEnabled(feature: string, defaultValue: boolean = false): boolean {
    const override = this.overrides[feature];
    
    if (override === undefined) {
      return defaultValue;
    }

    if (typeof override === 'boolean') {
      return override;
    }

    // Complex configuration
    if (typeof override === 'object') {
      // Check date range
      const now = new Date();
      if (override.startDate && new Date(override.startDate) > now) {
        return false;
      }
      if (override.endDate && new Date(override.endDate) < now) {
        return true; // Rollout complete
      }

      // Check specific users/establishments
      if (override.enabledUsers?.includes(this.userId || '')) {
        return true;
      }
      if (override.enabledEstablishments?.includes(this.establishmentId || '')) {
        return true;
      }

      // Check percentage rollout
      if (override.percentage !== undefined) {
        return this.isInPercentageRollout(feature, override.percentage);
      }
    }

    return defaultValue;
  }

  private isInPercentageRollout(feature: string, percentage: number): boolean {
    // Use consistent hashing based on user ID and feature name
    const hash = this.hashString(`${this.userId || 'anonymous'}-${feature}`);
    return (hash % 100) < percentage;
  }

  private hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  }

  setFeatureFlag(feature: string, config: boolean | FeatureFlagConfig) {
    this.overrides[feature] = config;
    this.saveToLocalStorage();
  }

  getFeatureFlags(): FeatureFlagOverride {
    return { ...this.overrides };
  }

  // Gradual rollout configuration
  configureGradualRollout(feature: string, config: {
    startPercentage: number;
    endPercentage: number;
    durationDays: number;
  }) {
    const startDate = new Date();
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + config.durationDays);

    this.setFeatureFlag(feature, {
      percentage: config.startPercentage,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    });

    // Schedule percentage updates
    this.scheduleRolloutUpdates(feature, config);
  }

  private scheduleRolloutUpdates(feature: string, config: { endPercentage: number; startPercentage: number; durationDays: number }) {
    const updateInterval = 24 * 60 * 60 * 1000; // Daily updates
    const percentageIncrease = (config.endPercentage - config.startPercentage) / config.durationDays;

    if (isTestEnv) {
      return;
    }

    const intervalId = setInterval(() => {
      const current = this.overrides[feature] as FeatureFlagConfig;
      if (!current || typeof current === 'boolean') {
        clearInterval(intervalId);
        return;
      }

      const newPercentage = Math.min(
        config.endPercentage,
        (current.percentage || 0) + percentageIncrease
      );

      this.setFeatureFlag(feature, {
        ...current,
        percentage: newPercentage,
      });

      if (newPercentage >= config.endPercentage) {
        clearInterval(intervalId);
      }
    }, updateInterval);
  }

  // Metrics tracking
  recordApiCall(module: string, isRealApi: boolean, responseTime: number, error?: Error) {
    if (!this.metrics[module]) {
      this.initializeModuleMetrics(module);
    }

    const metrics = this.metrics[module];
    if (!metrics) {
      return; // Safety check
    }
    
    if (isRealApi) {
      metrics.apiCalls++;
    } else {
      metrics.mockCalls++;
    }

    // Update average response time
    const totalCalls = metrics.apiCalls + metrics.mockCalls;
    metrics.avgResponseTime = 
      (metrics.avgResponseTime * (totalCalls - 1) + responseTime) / totalCalls;

    if (error) {
      metrics.errors++;
      metrics.lastError = error.message;
      metrics.lastErrorTimestamp = Date.now();
    }

    this.saveMetrics();
  }

  getMetrics(module?: string): Record<string, FeatureFlagMetrics> | FeatureFlagMetrics {
    if (module) {
      return this.metrics[module] || this.initializeModuleMetrics(module);
    }
    return { ...this.metrics };
  }

  getMetricsSummary() {
    const summary = {
      totalApiCalls: 0,
      totalMockCalls: 0,
      totalErrors: 0,
      avgResponseTime: 0,
      errorRate: 0,
      modules: {} as Record<string, FeatureFlagMetrics & { errorRate: number; apiUsageRate: number }>,
    };

    Object.entries(this.metrics).forEach(([module, metrics]) => {
      summary.totalApiCalls += metrics.apiCalls;
      summary.totalMockCalls += metrics.mockCalls;
      summary.totalErrors += metrics.errors;

      const totalCalls = metrics.apiCalls + metrics.mockCalls;
      summary.modules[module] = {
        ...metrics,
        errorRate: totalCalls > 0 ? (metrics.errors / totalCalls) * 100 : 0,
        apiUsageRate: totalCalls > 0 ? (metrics.apiCalls / totalCalls) * 100 : 0,
      };
    });

    const totalCalls = summary.totalApiCalls + summary.totalMockCalls;
    summary.avgResponseTime = 
      totalCalls > 0 
        ? Object.values(this.metrics).reduce((sum, m) => sum + m.avgResponseTime, 0) / Object.keys(this.metrics).length
        : 0;
    summary.errorRate = totalCalls > 0 ? (summary.totalErrors / totalCalls) * 100 : 0;

    return summary;
  }

  // Rollback functionality
  rollbackModule(module: string) {
    const featureKey = `USE_REAL_API.${module}`;
    this.setFeatureFlag(featureKey, false);
    
    // Log rollback
    console.warn(`Rolled back module ${module} to mock API`, {
      metrics: this.metrics[module],
      timestamp: new Date().toISOString(),
    });

    // Send rollback notification to monitoring service
    this.notifyRollback(module);
  }

  rollbackAll() {
    Object.keys(this.overrides).forEach(key => {
      if (key.startsWith('USE_REAL_API.')) {
        this.setFeatureFlag(key, false);
      }
    });

    console.warn('Rolled back all modules to mock API', {
      timestamp: new Date().toISOString(),
    });
  }

  // Export/Import configuration
  exportConfiguration() {
    return {
      overrides: this.overrides,
      metrics: this.metrics,
      timestamp: new Date().toISOString(),
    };
  }

  importConfiguration(config: { overrides?: FeatureFlagOverride; metrics?: Record<string, FeatureFlagMetrics> }) {
    if (config.overrides) {
      this.overrides = config.overrides;
      this.saveToLocalStorage();
    }
    if (config.metrics) {
      this.metrics = config.metrics;
      this.saveMetrics();
    }
  }

  // Private methods
  private initializeMetrics() {
    const modules = [
      'users', 'condominiums', 'supportTickets', 'maintenance',
      'documents', 'communications', 'governance', 'finance', 'areaReservations', 'assemblies'
    ];
    
    modules.forEach(module => this.initializeModuleMetrics(module));
  }

  private initializeModuleMetrics(module: string): FeatureFlagMetrics {
    this.metrics[module] = {
      apiCalls: 0,
      mockCalls: 0,
      errors: 0,
      avgResponseTime: 0,
    };
    return this.metrics[module];
  }

  private loadFromLocalStorage() {
    try {
      const saved = localStorage.getItem('mep_feature_flags');
      if (saved) {
        this.overrides = JSON.parse(saved);
      }

      const savedMetrics = localStorage.getItem('mep_feature_metrics');
      if (savedMetrics) {
        this.metrics = JSON.parse(savedMetrics);
      }
    } catch (error) {
      console.error('Failed to load feature flags from localStorage', error);
    }
  }

  private saveToLocalStorage() {
    try {
      localStorage.setItem('mep_feature_flags', JSON.stringify(this.overrides));
    } catch (error) {
      console.error('Failed to save feature flags to localStorage', error);
    }
  }

  private saveMetrics() {
    try {
      localStorage.setItem('mep_feature_metrics', JSON.stringify(this.metrics));
    } catch (error) {
      console.error('Failed to save metrics to localStorage', error);
    }
  }

  private notifyRollback(module: string) {
    // In a real implementation, this would send to a monitoring service
    if (window.console && window.console.error) {
      window.console.error('Module rollback notification', {
        module,
        metrics: this.metrics[module],
        timestamp: new Date().toISOString(),
      });
    }
  }
}

export const featureFlagManager = FeatureFlagManager.getInstance();

// React hook for feature flags
import { useState, useEffect } from 'react';

export function useManagedFeatureFlag(feature: string, defaultValue: boolean = false): boolean {
  const [enabled, setEnabled] = useState(() => 
    featureFlagManager.isFeatureEnabled(feature, defaultValue)
  );

  useEffect(() => {
    // Check for updates periodically
    if (!isTestEnv) {
      const interval = setInterval(() => {
        setEnabled(featureFlagManager.isFeatureEnabled(feature, defaultValue));
      }, 60000); // Check every minute

      return () => clearInterval(interval);
    }

    return undefined;
  }, [feature, defaultValue]);

  return enabled;
}

// Export types
export type { FeatureFlagConfig, FeatureFlagMetrics };
